import React from "react";
import { useTranslation } from "react-i18next";
import { Capacitor } from "@capacitor/core";
import { enabledProviders, OAuthProviderId, ProviderId } from "../services/authProviders";

export interface ProviderButtonsProps {
  /** "login" : connexion ; "link" : liaison à un compte existant (profil) */
  mode: "login" | "link";
  onOAuth: (id: OAuthProviderId) => void;
  onDiscord: () => void;
  onPhone?: () => void;
  /** Fournisseurs déjà liés (ids Firebase) pour les masquer en mode "link" */
  linked?: string[];
  disabled?: boolean;
}

const STYLE: Record<string, { icon: string; cls: string; label: string }> = {
  google: { icon: "fa-brands fa-google", cls: "bg-white text-gray-800", label: "Google" },
  apple: { icon: "fa-brands fa-apple", cls: "bg-black text-white border border-white/20", label: "Apple" },
  microsoft: { icon: "fa-brands fa-microsoft", cls: "bg-[#2f2f2f] text-white", label: "Microsoft" },
  facebook: { icon: "fa-brands fa-facebook", cls: "bg-[#1877F2] text-white", label: "Facebook" },
  discord: { icon: "fa-brands fa-discord", cls: "bg-[#5865F2] text-white", label: "Discord" },
  phone: { icon: "fa-solid fa-mobile-screen", cls: "bg-white/10 text-white border border-white/20", label: "" },
};

const FIREBASE_IDS: Record<string, string> = { google: "google.com", apple: "apple.com", microsoft: "microsoft.com", facebook: "facebook.com", phone: "phone" };

/** Grille de boutons « Continuer avec … », pilotée par REACT_APP_AUTH_PROVIDERS. */
const ProviderButtons: React.FC<ProviderButtonsProps> = ({ mode, onOAuth, onDiscord, onPhone, linked = [], disabled }) => {
  const { t } = useTranslation();
  const enabled = enabledProviders();
  const order: ProviderId[] = ["google", "apple", "microsoft", "discord", "facebook", "phone"];
  const visible = order.filter((p) => enabled.includes(p)).filter((p) => !(mode === "link" && FIREBASE_IDS[p] && linked.includes(FIREBASE_IDS[p]))).filter((p) => p !== "phone" || onPhone);
  if (!visible.length) return null;

  const isIosApple = (p: ProviderId) => p === "apple" && Capacitor.getPlatform() === "ios";
  const label = (p: ProviderId) => (p === "phone" ? t("auth.providers.phone") : t(mode === "link" ? "auth.providers.link" : "auth.providers.continueWith", { name: STYLE[p].label }));

  return (
    <div className="grid grid-cols-1 gap-2.5">
      {visible.map((p) => (
        <button
          key={p}
          type="button"
          disabled={disabled}
          onClick={() => (p === "discord" ? onDiscord() : p === "phone" ? onPhone && onPhone() : onOAuth(p as OAuthProviderId))}
          className={`w-full font-bold py-3.5 px-4 rounded-xl flex items-center justify-center gap-3 active:scale-[0.98] transition disabled:opacity-50 ${STYLE[p].cls} ${isIosApple(p) ? "order-first" : ""}`}>
          <i className={`${STYLE[p].icon} text-xl`} />
          <span className="text-sm" style={p === "google" ? { color: "#1f2937", WebkitTextFillColor: "#1f2937" } : undefined}>
            {label(p)}
          </span>
        </button>
      ))}
    </div>
  );
};

export default ProviderButtons;
